import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import WaitlistService from '../../services/waitlistService';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { useStore } from '../../context/StoreContext';
import { BRAND } from '../../theme/brandTokens';

const WaitlistOfferPage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const { storeId } = useStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [errorKey, setErrorKey] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const result = await WaitlistService.getOffer(token, storeId);
        if (!cancelled) setOffer(result);
      } catch (error) {
        if (!cancelled) setErrorKey('waitlistOfferPage.error.load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) load();
    return () => {
      cancelled = true;
    };
  }, [storeId, token]);

  const handleBook = async () => {
    try {
      setSubmitting(true);
      await WaitlistService.bookOffer(token, storeId);
      setOutcome('booked');
    } catch (error) {
      const code = error?.response?.data?.errorCode || error?.response?.data?.ErrorCode;
      setOutcome(code === 5021 || code === 'WaitlistOfferUnavailable' ? 'taken' : 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    try {
      setSubmitting(true);
      await WaitlistService.declineOffer(token, storeId);
      setOutcome('declined');
    } catch (error) {
      setOutcome('error');
    } finally {
      setSubmitting(false);
    }
  };

  const canBook = Boolean(offer?.canBook) && !outcome;

  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, maxWidth: 560, width: '100%', borderRadius: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('waitlistOfferPage.title')}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: BRAND.accent }} /></Box>
        ) : errorKey ? (
          <Alert severity="error">{t(errorKey)}</Alert>
        ) : outcome === 'booked' ? (
          <Alert severity="success">{t('waitlistOfferPage.success')}</Alert>
        ) : outcome === 'declined' ? (
          <Alert severity="info">{t('waitlistOfferPage.declined')}</Alert>
        ) : outcome === 'taken' || (offer && !offer.canBook) ? (
          <Alert severity="warning">{t('waitlistOfferPage.alreadyBooked')}</Alert>
        ) : (
          <Stack spacing={1.5}>
            <Typography>{offer?.storeName}</Typography>
            <Typography>
              {formatCalendarDate(offer?.date)} {String(offer?.from || '').slice(0, 5)}–{String(offer?.to || '').slice(0, 5)}
            </Typography>
            <Typography>{(offer?.serviceNames || []).join(', ')}</Typography>
            <Typography color="text.secondary">
              {t('waitlistOfferPage.duration', { minutes: offer?.durationMinutes })}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ pt: 1 }}>
              <Button color="inherit" onClick={handleDecline} disabled={submitting || !canBook}>
                {t('waitlistOfferPage.decline')}
              </Button>
              <PrimaryActionButton onClick={handleBook} disabled={submitting || !canBook}>
                {t('waitlistOfferPage.book')}
              </PrimaryActionButton>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default WaitlistOfferPage;
